import debounce from 'lodash/debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import {
  CreateNumberMaskReturn,
  FilterAccordion,
  hasDefinedValues,
  numberMask,
  removeMoneyMask,
  SwitchButton,
  TextField,
  TextFieldProps,
  useOnClickOutside,
} from '@avantstay/backoffice-core'

import * as S from '@new/__global/components/Filters/Filters.styles'

import { FiltersSharedProps } from '@/_new/__global/components/Filters/Filters.types'

export interface RangeFiltering {
  exact?: number
  min?: number
  max?: number
}

export interface RangeStringFiltering {
  exact?: string | number
  min?: string | number
  max?: string | number
}

interface RangeWithExactProps extends FiltersSharedProps, Omit<TextFieldProps, 'onChange'> {
  title: string
  onChange: (value?: RangeFiltering) => void
  value?: RangeFiltering
  filterId: string
  mask?: CreateNumberMaskReturn
  averageValue?: string
}

export const RangeWithExactFilter: FC<RangeWithExactProps> = ({
  title,
  isSidebarFilter,
  onChange,
  isAccordionOpen,
  value,
  filterId,
  mask = numberMask,
  averageValue,
  ...textFieldProps
}) => {
  const [localFilter, setLocalFilter] = useState<RangeStringFiltering | undefined>(value)

  const [isExactFilter, setIsExactFilter] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleSetAverage = () => {
    setLocalFilter(curr => ({ ...curr, max: averageValue }))
  }

  useEffect(() => {
    if (value) {
      setLocalFilter(value)
    }
  }, [value])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputOnChange = useCallback(
    debounce((value?: RangeStringFiltering) => {
      if (!value) return
      const { min, max, exact } = value

      const parsedValue = {
        min: min ? Number(min) : undefined,
        max: max ? Number(max) : undefined,
        exact: exact ? Number(exact) : undefined,
      }

      if (hasDefinedValues(parsedValue)) {
        onChange(parsedValue)
      } else {
        onChange(undefined)
      }
    }, 300),
    [],
  )

  const handleSubmitToValue = () => {
    if (value !== localFilter) {
      debouncedInputOnChange(localFilter)
    }
  }

  useOnClickOutside(wrapperRef, handleSubmitToValue)

  const minFilterText = localFilter?.min ? `Min: ${localFilter.min}` : undefined
  const maxFilterText = localFilter?.max ? `Max: ${localFilter.max}` : undefined
  const exactFilterText = localFilter?.exact ? `Exact: ${localFilter.exact}` : undefined

  const handleToggle = (value: boolean) => {
    setIsExactFilter(value)
  }

  const filtersValues = [minFilterText, maxFilterText, exactFilterText].filter(
    filter => filter !== undefined,
  )

  const handleOnClear = () => {
    setLocalFilter(undefined)
    onChange(undefined)
  }

  return (
    <FilterAccordion
      title={title}
      onClearFilter={handleOnClear}
      ref={wrapperRef}
      filterValue={filtersValues ?? value}
      hasMultipleValues
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
    >
      <S.RangeInputWrapper>
        {isExactFilter ? (
          <TextField.SmallMasked
            placeholder="Exact"
            value={localFilter?.exact || ''}
            mask={mask}
            onChange={({ currentTarget }) => {
              setLocalFilter({
                min: undefined,
                max: undefined,
                exact: removeMoneyMask(currentTarget.value),
              })
            }}
            {...textFieldProps}
          />
        ) : (
          <>
            <TextField.SmallMasked
              placeholder="Min"
              value={localFilter?.min || ''}
              mask={mask}
              onChange={({ currentTarget }) => {
                setLocalFilter(curr => ({
                  ...curr,
                  exact: undefined,
                  min: removeMoneyMask(currentTarget.value),
                }))
              }}
              {...textFieldProps}
            />
            <TextField.SmallMasked
              placeholder="Max"
              value={localFilter?.max || ''}
              mask={mask}
              onChange={({ currentTarget }) =>
                setLocalFilter(curr => ({
                  ...curr,
                  exact: undefined,
                  max: removeMoneyMask(currentTarget.value),
                }))
              }
              {...textFieldProps}
            />
          </>
        )}
      </S.RangeInputWrapper>
      <S.ActionsContainer>
        <SwitchButton
          title="Switch to exact values"
          id="exactFilter"
          checked={isExactFilter}
          onChange={({ target: { checked } }) => {
            handleToggle(checked)
          }}
        />
        {averageValue && !isExactFilter && (
          <S.SetAsAverage onClick={handleSetAverage}>Set Max as Average</S.SetAsAverage>
        )}
      </S.ActionsContainer>
    </FilterAccordion>
  )
}
